import { useCallback, useEffect, useState } from "react";
import {
  Container,
  Grid,
  IconButton,
  Pagination,
  Paper,
  Typography,
} from "@mui/material";
import TableGeneric from "../../components/Generics/TableGeneric";
import { GridColDef, GridSortModel } from "@mui/x-data-grid";
import { formatarData, useIsSmallScrenn } from "../../helper";
import { useSnack } from "../../redux/snack/snackHooks";
import Titulo2 from "../../components/Generics/Titulo2";
import VisibilityIcon from "@material-ui/icons/Visibility";
import LoadingBackdrop from "../../components/Generics/LoadingBackdrop";
import GridCenter from "../../components/Generics/GridCenter";
import dayjs from "dayjs";
import apiSolicitacaoEncerramentoMDFe from "../../api/solicitacaoEncerramentoMDFeApi";
import { SolicitacaoEncerramentoMDFe } from "../../model/entities/SolicitacaoEncerramentoMDFe";
import ModalEncerramentoMDFe from "../../components/modais/ModalEncerramentoMDFe";

type Props = {};

const SolicitacaoMDFePage: React.FC<Props> = (props) => {
  const isSmallScrenn = useIsSmallScrenn();
  const [solicitacoes, setSolicitacoes] = useState<
    SolicitacaoEncerramentoMDFe[]
  >([]);
  const [totalLinhas, setTotalLinhas] = useState(4);
  const [loading, setLoading] = useState(false);
  const [pagina, setPagina] = useState(1);

  const [linhasPorPagina, setLinhasPorPagina] = useState(0);
  const [solicitacaoSelecionada, setSolicitacaoSelecionada] =
    useState<SolicitacaoEncerramentoMDFe | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const mostrarSnack = useSnack();
  const rowHeight = 52;

  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "id", sort: "desc" },
  ]);

  useEffect(() => {
    setLoading(true);
    const calcularPageSize = () => {
      const alturaHeader = 56;
      const alturaFooter = 52;
      const alturaDisponivel =
        window.innerHeight - alturaHeader - alturaFooter - 200;

      const linhasVisiveis = Math.floor(alturaDisponivel / rowHeight);
      setLinhasPorPagina(linhasVisiveis > 0 ? linhasVisiveis : 1);
      setLoading(false);
    };

    calcularPageSize();
    window.addEventListener("resize", calcularPageSize);

    return () => {
      window.removeEventListener("resize", calcularPageSize);
    };
  }, []);

  const buscarSolicitacoes = useCallback(async () => {
    try {
      setLoading(true);

      const resTotal = await apiSolicitacaoEncerramentoMDFe.count();
      setTotalLinhas(resTotal);

      let field = sortModel[0]?.field ?? "id";

      if (field === "nomeMotorista") {
        field = "motorista";
      }
      const res = await apiSolicitacaoEncerramentoMDFe.getMany(
        pagina - 1,
        linhasPorPagina,
        field,
        sortModel[0]?.sort ?? "asc"
      );

      setSolicitacoes(res);
      setLoading(false);
    } catch (erro: any) {
      mostrarSnack(
        "Ops); Aconteceu um problema ao buscar os solicitacoes !!",
        "error"
      );
      console.log("erro ao buscar solicitacoes", erro);
      setLoading(false);
    }
  }, [pagina, linhasPorPagina, sortModel]);

  useEffect(() => {
    buscarSolicitacoes();
  }, [pagina, linhasPorPagina, sortModel]);

  const columns: GridColDef<SolicitacaoEncerramentoMDFe>[] = [
    {
      field: "id",
      headerName: "ID",
      width: 50,
      editable: false,
    },
    {
      field: "nomeMotorista",
      headerName: "Motorista",
      width: 260,
      editable: false,
    },
    {
      field: "numeroMDFe",
      headerName: "Número do MDF-e",
      width: 200,
      editable: false,
    },
    {
      field: "placaVeiculo",
      headerName: "Placa do Veículo",
      width: 150,
      editable: false,
    },
    {
      field: "dataHoraSolicitacao",
      headerName: "Data",
      editable: false,
      renderCell: (params) => {
        return formatarData(params.row.dataHoraSolicitacao.toString());
      },
    },

    {
      field: "acoes",
      headerName: "Ações",
      width: 80,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        const solicitacao = params.row;
        return (
          <IconButton onClick={(e) => e.stopPropagation()}>
            <VisibilityIcon
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                setSolicitacaoSelecionada(solicitacao);
                setOpenModal(true);
              }}
            />
          </IconButton>
        );
      },
    },
  ];

  if (loading) {
    return <LoadingBackdrop open={loading} />;
  }

  return (
    <Grid spacing={2} style={{ display: "flex", justifyContent: "center" }}>
      <Container style={{ marginBottom: "15px" }}>
        <Grid item xs={12} style={{ textAlign: "center" }}>
          <Titulo2>Solicitações para encerramento de MDF-e </Titulo2>
        </Grid>

        {isSmallScrenn ? (
          <>
            {solicitacoes.map((solicitacao, index) => (
              <GridCenter key={index + 1}>
                <Paper
                  style={{
                    margin: "12px 0px",
                    padding: "12px",
                    width: "100%",
                    borderRadius: "16px",
                  }}
                  key={solicitacao.id}
                >
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography fontWeight={"bold"}>
                        {solicitacao.nomeMotorista}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography>
                        Número da MDFe: {solicitacao.numeroMDFe}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography>
                        Placa do Veículo: {solicitacao.placaVeiculo}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography>
                        Data Solicitação{" "}
                        {dayjs(solicitacao.dataHoraSolicitacao).format(
                          "DD/MM/YYYY - HH:MM"
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} style={{ textAlign: "right" }}>
                      <IconButton>
                        <VisibilityIcon
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSolicitacaoSelecionada(solicitacao);
                            setOpenModal(true);
                          }}
                        />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              </GridCenter>
            ))}
            <GridCenter>
              <Pagination
                size={"large"}
                defaultPage={1}
                count={totalLinhas / linhasPorPagina}
                onChange={(event, number) => setPagina(number)}
                page={pagina}
              />
            </GridCenter>
          </>
        ) : (
          <Grid item xs={12}>
            <TableGeneric
              pageSize={linhasPorPagina}
              colunas={columns}
              linhas={solicitacoes}
              loading={loading}
              totalLinhas={totalLinhas}
              selecionaLinha={false}
              paginaAtual={pagina - 1}
              sx={{ height: "50%" }}
              rowHeight={52}
              sortModel={sortModel}
              setSortModel={setSortModel}
              onRowClick={(params) => {
                setSolicitacaoSelecionada(params.row);
                setOpenModal(true);
              }}
              formasDeListar={[5, 10]}
              setPaginacao={(model) => {
                setPagina(model.page);
                setLinhasPorPagina(model.pageSize);
              }}
            />
          </Grid>
        )}
      </Container>

      {openModal && solicitacaoSelecionada && (
        <ModalEncerramentoMDFe
          open={openModal}
          solicitacao={solicitacaoSelecionada}
          onClose={() => {
            buscarSolicitacoes();
            setOpenModal(false);
            setSolicitacaoSelecionada(null);
          }}
        />
      )}
    </Grid>
  );
};

export default SolicitacaoMDFePage;
